import React, { useEffect, useMemo, useState } from 'react';
import { X, ClipboardList, Calendar, Clock, User, Link as LinkIcon, AlertCircle, Pencil } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { useAppData } from '../../contexts/AppDataContext';
export interface TaskData {
  title: string;
  description: string;
  assignee: string;
  dueDate: string;
  dueTime: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  linkedTo?: {
    id: string;
    type: 'contact' | 'subscription' | 'order';
    label: string;
  };
  status: 'Open';
}
export interface EditTaskData {
  id: string;
  title: string;
  description?: string;
  assignee: string;
  dueDate: string;
  dueTime?: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  linkedTo?: {
    id: string;
    type: string;
    label?: string;
  } | null;
}
interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (task: TaskData) => void;
  onUpdate?: (taskId: string, task: TaskData) => void;
  editTask?: EditTaskData | null;
  initialLinkedTo?: {
    id: string;
    type: 'contact' | 'subscription' | 'order';
    label: string;
  };
}
const TEAM_MEMBERS = ['Current User', 'Sarah Admin', 'Mike Johnson', 'Chris Taylor', 'Sarah Chen'];
function parseDateToISO(dateStr: string): string {
  // Try parsing dates like "Feb 12 2026" or "Feb 18 2026"
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  // If already ISO format, return as-is
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.split('T')[0];
  }
  return new Date().toISOString().split('T')[0];
}
export function CreateTaskModal({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  editTask,
  initialLinkedTo
}: CreateTaskModalProps) {
  const {
    subscriptions
  } = useAppData();
  const isEditMode = !!editTask;
  const [formData, setFormData] = useState<TaskData>({
    title: '',
    description: '',
    assignee: 'Current User',
    dueDate: new Date().toISOString().split('T')[0],
    dueTime: '',
    priority: 'Medium',
    status: 'Open',
    linkedTo: initialLinkedTo
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [showLinkResults, setShowLinkResults] = useState(false);
  // Deduplicate customers by email and filter by search query
  const linkSearchResults = useMemo(() => {
    if (!linkSearchQuery.trim()) return [];
    const query = linkSearchQuery.toLowerCase();
    const seen = new Set<string>();
    const results: Array<{
      id: string;
      type: 'contact' | 'subscription';
      label: string;
      subLabel: string;
    }> = [];
    for (const sub of subscriptions) {
      if (!seen.has(sub.customer)) {
        if (sub.customerName.toLowerCase().includes(query) || sub.customer.toLowerCase().includes(query)) {
          seen.add(sub.customer);
          results.push({
            id: sub.id,
            type: 'contact',
            label: sub.customerName,
            subLabel: sub.customer
          });
        }
      }
      if (sub.id.toLowerCase().includes(query) || sub.subscriptionTitle.toLowerCase().includes(query)) {
        results.push({
          id: sub.id,
          type: 'subscription',
          label: sub.id,
          subLabel: `${sub.customerName} — ${sub.subscriptionTitle}`
        });
      }
    }
    return results.slice(0, 8);
  }, [linkSearchQuery, subscriptions]);
  // Reset form when modal opens — pre-fill if editing
  useEffect(() => {
    if (isOpen) {
      if (editTask) {
        setFormData({
          title: editTask.title,
          description: editTask.description || '',
          assignee: editTask.assignee,
          dueDate: parseDateToISO(editTask.dueDate),
          dueTime: editTask.dueTime || '',
          priority: editTask.priority,
          status: 'Open',
          linkedTo: editTask.linkedTo ? {
            id: editTask.linkedTo.id,
            type: editTask.linkedTo.type as 'contact' | 'subscription' | 'order',
            label: editTask.linkedTo.label || editTask.linkedTo.id
          } : undefined
        });
      } else {
        setFormData({
          title: '',
          description: '',
          assignee: 'Current User',
          dueDate: new Date().toISOString().split('T')[0],
          dueTime: '',
          priority: 'Medium',
          status: 'Open',
          linkedTo: initialLinkedTo
        });
      }
      setErrors({});
      setLinkSearchQuery('');
      setShowLinkResults(false);
    }
  }, [isOpen, initialLinkedTo, editTask]);
  if (!isOpen) return null;
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.assignee) newErrors.assignee = 'Assignee is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      if (isEditMode && onUpdate && editTask) {
        onUpdate(editTask.id, formData);
      } else {
        onCreate(formData);
      }
      onClose();
    }
  };
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[rgba(15,23,42,0.5)] z-[1040]" onClick={onClose} data-id="element-1259" />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1050] p-4" data-id="element-1260">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-[560px] animate-[ocean-scale-in_200ms_ease-in-out] flex flex-col max-h-[calc(100vh-40px)]" data-id="element-1261">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]" data-id="element-1262">
            <div className="flex items-center gap-3" data-id="element-1263">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEditMode ? 'bg-[#FEF3C7]' : 'bg-[#E0F2FE]'}`} data-id="element-1264">
                {isEditMode ? <Pencil className="w-5 h-5 text-[#F59E0B]" data-id="element-1265" /> : <ClipboardList className="w-5 h-5 text-[#0EA5E9]" data-id="element-1266" />}
              </div>
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-1267">
                {isEditMode ? 'Edit Task' : 'Create Task'}
              </h2>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors" data-id="element-1268">
              <X className="w-5 h-5" data-id="element-1269" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6 overflow-y-auto" data-id="element-1270">
            <div className="space-y-5" data-id="element-1271">
              {/* Title */}
              <div data-id="element-1272">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1273">
                  Title <span className="text-[#EF4444]" data-id="element-1274">*</span>
                </label>
                <input type="text" value={formData.title} onChange={e => setFormData({
                ...formData,
                title: e.target.value
              })} placeholder="e.g. Follow up on failed payment" className={`w-full h-10 px-3 border rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 transition-all ${errors.title ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#CBD5E1] focus:border-[#0EA5E9]'}`} data-id="element-1275" />
                {errors.title && <p className="mt-1 text-[12px] text-[#EF4444] flex items-center gap-1" data-id="element-1276">
                    <AlertCircle className="w-3 h-3" data-id="element-1277" /> {errors.title}
                  </p>}
              </div>

              {/* Description */}
              <div data-id="element-1278">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1279">
                  Description
                </label>
                <textarea value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} placeholder="Add details or context..." rows={3} className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all resize-none" data-id="element-1280" />
              </div>

              {/* Assignee */}
              <div data-id="element-1281">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1282">
                  Assignee <span className="text-[#EF4444]" data-id="element-1283">*</span>
                </label>
                <div className="relative" data-id="element-1284">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-1285" />
                  <select value={formData.assignee} onChange={e => setFormData({
                  ...formData,
                  assignee: e.target.value
                })} className="w-full h-10 pl-9 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none" data-id="element-1286">
                    {TEAM_MEMBERS.map(member => <option key={member} value={member} data-id="element-1287">
                        {member}
                      </option>)}
                  </select>
                </div>
              </div>

              {/* Due Date & Time */}
              <div className="grid grid-cols-2 gap-4" data-id="element-1288">
                <div data-id="element-1289">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1290">
                    Due Date <span className="text-[#EF4444]" data-id="element-1291">*</span>
                  </label>
                  <div className="relative" data-id="element-1292">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-1293" />
                    <input type="date" value={formData.dueDate} onChange={e => setFormData({
                    ...formData,
                    dueDate: e.target.value
                  })} className={`w-full h-10 pl-9 pr-3 border rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 transition-all ${errors.dueDate ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#CBD5E1] focus:border-[#0EA5E9]'}`} data-id="element-1294" />
                  </div>
                </div>
                <div data-id="element-1295">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1296">
                    Due Time
                  </label>
                  <div className="relative" data-id="element-1297">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-1298" />
                    <input type="time" value={formData.dueTime} onChange={e => setFormData({
                    ...formData,
                    dueTime: e.target.value
                  })} className="w-full h-10 pl-9 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all" data-id="element-1299" />
                  </div>
                </div>
              </div>

              {/* Priority */}
              <div data-id="element-1300">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1301">
                  Priority <span className="text-[#EF4444]" data-id="element-1302">*</span>
                </label>
                <div className="flex bg-[#F1F5F9] p-1 rounded-lg" data-id="element-1303">
                  {['Low', 'Medium', 'High', 'Urgent'].map(priority => <button key={priority} type="button" onClick={() => setFormData({
                  ...formData,
                  priority: priority as any
                })} className={`flex-1 py-1.5 text-[13px] font-medium rounded-md transition-all ${formData.priority === priority ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-1304">
                      {priority}
                    </button>)}
                </div>
              </div>

              {/* Linked To */}
              <div data-id="element-1305">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1306">
                  Linked To
                </label>
                {formData.linkedTo ? <div className="flex items-center gap-2 p-2 bg-[#F0F9FF] border border-[#BAE6FD] rounded-md text-[#0284C7]" data-id="element-1307">
                    <LinkIcon className="w-4 h-4" data-id="element-1308" />
                    <span className="text-[13px] font-medium flex-1" data-id="element-1309">
                      {formData.linkedTo.label}
                    </span>
                    <button onClick={() => setFormData({
                  ...formData,
                  linkedTo: undefined
                })} className="p-1 hover:bg-[#E0F2FE] rounded-full transition-colors" data-id="element-1310">
                      <X className="w-3 h-3" data-id="element-1311" />
                    </button>
                  </div> : <div className="relative" data-id="element-1312">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" data-id="element-1313" />
                    <input type="text" value={linkSearchQuery} onChange={e => {
                  setLinkSearchQuery(e.target.value);
                  setShowLinkResults(true);
                }} onFocus={() => {
                  if (linkSearchQuery.trim()) setShowLinkResults(true);
                }} onBlur={() => {
                  setTimeout(() => setShowLinkResults(false), 200);
                }} placeholder="Search contacts, subscriptions, or orders..." className="w-full h-10 pl-9 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all" data-id="element-1314" />

                    {showLinkResults && linkSearchResults.length > 0 && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 max-h-[240px] overflow-y-auto" data-id="element-1315">
                        {linkSearchResults.map((result, idx) => <button key={`${result.type}-${result.id}-${idx}`} type="button" onMouseDown={e => {
                    e.preventDefault();
                    setFormData({
                      ...formData,
                      linkedTo: {
                        id: result.id,
                        type: result.type,
                        label: result.type === 'contact' ? `${result.label} (${result.subLabel})` : `${result.label} — ${result.subLabel}`
                      }
                    });
                    setLinkSearchQuery('');
                    setShowLinkResults(false);
                  }} className="w-full px-3 py-2.5 text-left hover:bg-[#F8FAFC] transition-colors flex items-center gap-3 border-b border-[#F1F5F9] last:border-b-0" data-id="element-1316">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${result.type === 'contact' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'bg-[#EDE9FE] text-[#6F42C1]'}`} data-id="element-1317">
                              {result.type === 'contact' ? <User className="w-3.5 h-3.5" data-id="element-1318" /> : <LinkIcon className="w-3.5 h-3.5" data-id="element-1319" />}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1320">
                              <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-1321">
                                {result.label}
                              </div>
                              <div className="text-[12px] text-[#94A3B8] truncate" data-id="element-1322">
                                {result.subLabel}
                              </div>
                            </div>
                            <span className="text-[10px] font-medium text-[#94A3B8] uppercase tracking-wider flex-shrink-0" data-id="element-1323">
                              {result.type === 'contact' ? 'Contact' : 'Subscription'}
                            </span>
                          </button>)}
                      </div>}

                    {showLinkResults && linkSearchQuery.trim().length > 0 && linkSearchResults.length === 0 && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 px-4 py-3" data-id="element-1324">
                          <p className="text-[13px] text-[#94A3B8] text-center" data-id="element-1325">
                            No results found for "{linkSearchQuery}"
                          </p>
                        </div>}
                  </div>}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E2E8F0] mt-auto" data-id="element-1326">
            <Button variant="outline" size="md" onClick={onClose} data-id="element-1327">
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={handleSubmit} data-id="element-1328">
              {isEditMode ? 'Save Changes' : 'Create Task'}
            </Button>
          </div>
        </div>
      </div>
    </>;
}